import React, { useEffect, useState } from 'react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { coworkers } from '../utils/mockData';
import { CameraIcon, FileIcon, ArrowLeftIcon, ArrowRightIcon, CalendarIcon, MapPinIcon, DollarSignIcon, UserIcon, CheckIcon, CoffeeIcon, HotelIcon, CarIcon, ShoppingBagIcon, PlaneIcon, BriefcaseIcon, GiftIcon, HeadphonesIcon, WifiIcon, PackageIcon, XIcon, ZoomInIcon, MaximizeIcon, MonitorIcon } from 'lucide-react';
import AddEmployeeModal from '../components/AddEmployeeModal';
// Define expense types
const EXPENSE_TYPES = {
  MEAL: 'meal',
  LODGING: 'lodging',
  TRANSPORTATION: 'transportation',
  AIRFARE: 'airfare',
  SUPPLIES: 'supplies',
  ENTERTAINMENT: 'entertainment',
  TELECOM: 'telecom',
  CLIENT_GIFT: 'client gift',
  EQUIPMENT: 'equipment',
  INTERNET: 'internet',
  BUSINESS_SERVICES: 'business services',
  OTHER: 'other'
};
// Keywords for expense classification
const EXPENSE_KEYWORDS = {
  [EXPENSE_TYPES.MEAL]: ['restaurant', 'cafe', 'coffee', 'diner', 'bistro', 'bar', 'grill', 'eatery', 'food', 'meal', 'lunch', 'dinner', 'breakfast'],
  [EXPENSE_TYPES.LODGING]: ['hotel', 'motel', 'inn', 'lodging', 'airbnb', 'accommodation', 'stay'],
  [EXPENSE_TYPES.TRANSPORTATION]: ['taxi', 'uber', 'lyft', 'car rental', 'gas', 'fuel', 'train', 'bus', 'metro'],
  [EXPENSE_TYPES.AIRFARE]: ['flight', 'airline', 'airport', 'airfare'],
  [EXPENSE_TYPES.SUPPLIES]: ['office', 'supplies', 'stationery', 'equipment', 'hardware', 'software']
};
export function UploadReceipt({
  step,
  nextStep,
  prevStep,
  navigateTo
}) {
  const [selectedCoworkers, setSelectedCoworkers] = useState([]);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [detectedExpenseType, setDetectedExpenseType] = useState(null);
  const [selectedExpenseType, setSelectedExpenseType] = useState(null);
  const [merchantName, setMerchantName] = useState('Cafe Milano');
  const [expenseDate, setExpenseDate] = useState('October 15, 2025');
  const [expenseAmount, setExpenseAmount] = useState('$24.85');
  const [showExpenseTypeSelector, setShowExpenseTypeSelector] = useState(false);
  const [showFullSizeReceipt, setShowFullSizeReceipt] = useState(false);
  // Receipt image URL - realistic receipt photo
  const receiptImageUrl = 'https://images.pexels.com/photos/3483098/pexels-photo-3483098.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2';
  // Get the effective expense type (user selected or detected)
  const expenseType = selectedExpenseType || detectedExpenseType;
  // Determine if step 3 (people) should be shown based on expense type
  const shouldShowPeopleStep = expenseType === EXPENSE_TYPES.MEAL;
  // Function to classify expense based on merchant name
  const classifyExpense = merchant => {
    const merchantLower = merchant.toLowerCase();
    // Check each expense type's keywords
    for (const [type, keywords] of Object.entries(EXPENSE_KEYWORDS)) {
      for (const keyword of keywords) {
        if (merchantLower.includes(keyword)) {
          return type;
        }
      }
    }
    // Default to OTHER if no match found
    return EXPENSE_TYPES.OTHER;
  };
  // Classify expense when merchant name changes
  useEffect(() => {
    if (merchantName) {
      const detectedType = classifyExpense(merchantName);
      setDetectedExpenseType(detectedType);
      // Only auto-set the selected type if user hasn't manually chosen one
      if (!selectedExpenseType) {
        setSelectedExpenseType(detectedType);
      }
    }
  }, [merchantName]);
  // Custom next step function to skip step 3 for non-meal expenses
  const handleNextStep = () => {
    if (step === 2 && !shouldShowPeopleStep) {
      // Instead of calling nextStep twice, directly navigate to step 4
      // by passing the value to the parent component
      navigateTo('upload', 4);
    } else {
      nextStep();
    }
  };
  const toggleCoworker = id => {
    if (selectedCoworkers.includes(id)) {
      setSelectedCoworkers(selectedCoworkers.filter(coworkerId => coworkerId !== id));
    } else {
      setSelectedCoworkers([...selectedCoworkers, id]);
    }
  };
  const handleAddEmployee = employee => {
    const newCoworker = {
      id: employee.id,
      name: employee.name,
      avatar: employee.avatar
    };
    setSelectedCoworkers([...selectedCoworkers, employee.id]);
    // Add the new employee to the coworkers list if they're not already in it
    if (!coworkers.find(c => c.id === employee.id)) {
      coworkers.push(newCoworker);
    }
  };
  // Select expense type and close selector
  const handleSelectExpenseType = type => {
    setSelectedExpenseType(type);
    setShowExpenseTypeSelector(false);
  };
  // Get expense type icon
  const getExpenseTypeIcon = type => {
    switch (type) {
      case EXPENSE_TYPES.MEAL:
        return <CoffeeIcon size={18} className="text-blue-500" data-id="element-318" />;
      case EXPENSE_TYPES.LODGING:
        return <HotelIcon size={18} className="text-purple-500" data-id="element-319" />;
      case EXPENSE_TYPES.TRANSPORTATION:
        return <CarIcon size={18} className="text-green-500" data-id="element-320" />;
      case EXPENSE_TYPES.AIRFARE:
        return <PlaneIcon size={18} className="text-teal-500" data-id="element-321" />;
      case EXPENSE_TYPES.SUPPLIES:
        return <ShoppingBagIcon size={18} className="text-orange-500" data-id="element-322" />;
      case EXPENSE_TYPES.ENTERTAINMENT:
        return <HeadphonesIcon size={18} className="text-pink-500" data-id="element-323" />;
      case EXPENSE_TYPES.TELECOM:
        return <WifiIcon size={18} className="text-indigo-500" data-id="element-324" />;
      case EXPENSE_TYPES.CLIENT_GIFT:
        return <GiftIcon size={18} className="text-red-500" data-id="element-325" />;
      case EXPENSE_TYPES.EQUIPMENT:
        return <MonitorIcon size={18} className="text-yellow-600" data-id="element-326" />;
      case EXPENSE_TYPES.INTERNET:
        return <WifiIcon size={18} className="text-cyan-500" data-id="element-327" />;
      case EXPENSE_TYPES.BUSINESS_SERVICES:
        return <BriefcaseIcon size={18} className="text-gray-700" data-id="element-328" />;
      default:
        return <PackageIcon size={18} className="text-gray-500" data-id="element-329" />;
    }
  };
  // Full size receipt modal component
  const FullSizeReceiptModal = () => {
    if (!showFullSizeReceipt) return null;
    return <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4" onClick={() => setShowFullSizeReceipt(false)} data-id="element-330">
        <div className="relative max-w-4xl max-h-full" data-id="element-331">
          <button className="absolute top-4 right-4 bg-black bg-opacity-50 rounded-full p-2 text-white" onClick={e => {
          e.stopPropagation();
          setShowFullSizeReceipt(false);
        }} data-id="element-332">
            <XIcon size={24} data-id="element-333" />
          </button>
          <img src={receiptImageUrl} alt="Receipt full view" className="max-w-full max-h-[90vh] object-contain rounded-lg" data-id="element-334" />
        </div>
      </div>;
  };
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <div className="space-y-4" data-id="element-335">
            <h2 className="text-xl font-bold text-center" data-id="element-336">Upload Receipt</h2>
            <p className="text-gray-600 text-center mb-6" data-id="element-337">
              Take a photo or upload an image of your receipt
            </p>
            <Button variant="outline" fullWidth className="mb-3" icon={<CameraIcon size={20} data-id="element-339" />} onClick={nextStep} data-id="element-338">
              Take Photo
            </Button>
            <Button variant="outline" fullWidth icon={<FileIcon size={20} data-id="element-341" />} onClick={nextStep} data-id="element-340">
              Upload Image
            </Button>
            <div className="mt-8 border-t border-gray-200 pt-4" data-id="element-342">
              <p className="text-sm text-gray-500 text-center" data-id="element-343">
                For best results, make sure the receipt is flat and well-lit
              </p>
            </div>
          </div>;
      case 2:
        return <div className="space-y-4" data-id="element-344">
            <h2 className="text-xl font-bold text-center" data-id="element-345">Receipt Details</h2>
            <p className="text-gray-600 text-center mb-4" data-id="element-346">
              We've extracted the following information
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-6 relative" data-id="element-347">
              <div className="relative cursor-pointer transform transition-transform hover:scale-[1.02] active:scale-[0.98]" onClick={() => setShowFullSizeReceipt(true)} data-id="element-348">
                <img src={receiptImageUrl} alt="Receipt preview" className="w-full h-48 object-cover rounded-lg mb-3 shadow-md rotate-1" style={{
                filter: 'contrast(1.05) brightness(1.02)'
              }} data-id="element-349" />
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full" data-id="element-350">
                  <MaximizeIcon size={16} data-id="element-351" />
                </div>
              </div>
            </div>
            <Card className="mb-2" data-id="element-352">
              <div className="flex items-center" data-id="element-353">
                <CalendarIcon size={18} className="text-gray-400 mr-2" data-id="element-354" />
                <div data-id="element-355">
                  <p className="text-sm text-gray-500" data-id="element-356">Date</p>
                  <p className="font-medium" data-id="element-357">{expenseDate}</p>
                </div>
              </div>
            </Card>
            <Card className="mb-2" data-id="element-358">
              <div className="flex items-center" data-id="element-359">
                <MapPinIcon size={18} className="text-gray-400 mr-2" data-id="element-360" />
                <div data-id="element-361">
                  <p className="text-sm text-gray-500" data-id="element-362">Location</p>
                  <p className="font-medium" data-id="element-363">
                    <input type="text" value={merchantName} onChange={e => setMerchantName(e.target.value)} className="font-medium focus:outline-none w-full" data-id="element-364" />
                  </p>
                </div>
              </div>
            </Card>
            <Card className="mb-4" data-id="element-365">
              <div className="flex items-center" data-id="element-366">
                <DollarSignIcon size={18} className="text-gray-400 mr-2" data-id="element-367" />
                <div data-id="element-368">
                  <p className="text-sm text-gray-500" data-id="element-369">Total Amount</p>
                  <p className="font-medium" data-id="element-370">{expenseAmount}</p>
                </div>
              </div>
            </Card>
            {/* Expense Type Selector */}
            <Card className="mb-2 cursor-pointer" onClick={() => setShowExpenseTypeSelector(!showExpenseTypeSelector)} data-id="element-371">
              <div className="flex items-center justify-between" data-id="element-372">
                <div className="flex items-center" data-id="element-373">
                  {expenseType && getExpenseTypeIcon(expenseType)}
                  <div className="ml-2" data-id="element-374">
                    <p className="text-sm text-gray-500" data-id="element-375">Expense Type</p>
                    <p className="font-medium capitalize" data-id="element-376">{expenseType}</p>
                  </div>
                </div>
                <div className="text-blue-600 text-sm" data-id="element-377">
                  {showExpenseTypeSelector ? 'Close' : 'Change'}
                </div>
              </div>
            </Card>
            {/* Expense Type Options */}
            {showExpenseTypeSelector && <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm mb-4" data-id="element-378">
                <p className="text-sm text-gray-500 mb-2" data-id="element-379">
                  Select expense type:
                </p>
                <div className="grid grid-cols-2 gap-2" data-id="element-380">
                  {Object.values(EXPENSE_TYPES).map(type => <div key={type} className={`flex items-center p-2 rounded-lg cursor-pointer ${type === selectedExpenseType ? 'bg-blue-50 border border-blue-200' : 'border border-gray-200 hover:bg-gray-50'}`} onClick={() => handleSelectExpenseType(type)} data-id="element-381">
                      <div className="mr-2" data-id="element-382">{getExpenseTypeIcon(type)}</div>
                      <span className="text-sm capitalize" data-id="element-383">{type}</span>
                      {type === selectedExpenseType && <CheckIcon size={14} className="ml-auto text-blue-600" data-id="element-384" />}
                    </div>)}
                </div>
                <p className="text-xs text-gray-400 mt-2" data-id="element-385">
                  Note: Only meal expenses require attendee information
                </p>
              </div>}
          </div>;
      case 3:
        return <div className="space-y-4" data-id="element-386">
            <h2 className="text-xl font-bold text-center" data-id="element-387">Who Was Present?</h2>
            <p className="text-gray-600 text-center mb-4" data-id="element-388">
              Select all colleagues who shared this meal with you
            </p>
            {coworkers.map(coworker => <div key={coworker.id} className={`flex items-center p-3 rounded-lg mb-2 border ${selectedCoworkers.includes(coworker.id) ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`} onClick={() => toggleCoworker(coworker.id)} data-id="element-389">
                <img src={coworker.avatar} alt={coworker.name} className="w-10 h-10 rounded-full mr-3" data-id="element-390" />
                <span className="flex-1" data-id="element-391">{coworker.name}</span>
                {selectedCoworkers.includes(coworker.id) && <CheckIcon size={18} className="text-blue-600" data-id="element-392" />}
              </div>)}
            <div className="pt-2" data-id="element-393">
              <Button variant="outline" fullWidth icon={<UserIcon size={18} data-id="element-395" />} onClick={() => setShowAddEmployee(true)} data-id="element-394">
                Add Someone Else
              </Button>
            </div>
            {showAddEmployee && <AddEmployeeModal onClose={() => setShowAddEmployee(false)} onAddEmployee={handleAddEmployee} data-id="element-396" />}
          </div>;
      case 4:
        return <div className="space-y-4 text-center" data-id="element-397">
            <div className="flex justify-center mb-2" data-id="element-398">
              <div className="bg-green-100 p-4 rounded-full" data-id="element-399">
                <CheckIcon size={40} className="text-green-600" data-id="element-400" />
              </div>
            </div>
            <h2 className="text-xl font-bold" data-id="element-401">All Set!</h2>
            <p className="text-gray-600 mb-6" data-id="element-402">
              Your receipt has been submitted successfully
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-4" data-id="element-403">
              <div className="flex justify-between mb-2" data-id="element-404">
                <span className="text-gray-500" data-id="element-405">Merchant:</span>
                <span className="font-medium" data-id="element-406">{merchantName}</span>
              </div>
              <div className="flex justify-between mb-2" data-id="element-407">
                <span className="text-gray-500" data-id="element-408">Date:</span>
                <span className="font-medium" data-id="element-409">{expenseDate}</span>
              </div>
              <div className="flex justify-between mb-2" data-id="element-410">
                <span className="text-gray-500" data-id="element-411">Amount:</span>
                <span className="font-medium" data-id="element-412">{expenseAmount}</span>
              </div>
              <div className="flex justify-between mb-2" data-id="element-413">
                <span className="text-gray-500" data-id="element-414">Type:</span>
                <div className="flex items-center font-medium capitalize" data-id="element-415">
                  <span className="mr-2" data-id="element-416">{expenseType}</span>
                  {getExpenseTypeIcon(expenseType)}
                </div>
              </div>
              {shouldShowPeopleStep && <div className="flex justify-between" data-id="element-417">
                  <span className="text-gray-500" data-id="element-418">People:</span>
                  <span className="font-medium" data-id="element-419">
                    {selectedCoworkers.length} colleague(s)
                  </span>
                </div>}
            </div>
            <Button onClick={() => navigateTo('home')} fullWidth data-id="element-420">
              Back to Home
            </Button>
          </div>;
      default:
        return null;
    }
  };
  const renderNavButtons = () => {
    if (step === 1 || step === 4) return null;
    return <div className="flex justify-between mt-8" data-id="element-421">
        <Button variant="outline" onClick={prevStep} icon={<ArrowLeftIcon size={16} data-id="element-423" />} data-id="element-422">
          Back
        </Button>
        <Button onClick={handleNextStep} icon={<ArrowRightIcon size={16} data-id="element-425" />} className="ml-auto" data-id="element-424">
          {step === 3 || step === 2 && !shouldShowPeopleStep ? 'Submit' : 'Next'}
        </Button>
      </div>;
  };
  // Calculate the total number of steps (3 or 4 depending on expense type)
  const totalSteps = shouldShowPeopleStep ? 4 : 3;
  // Adjust the progress indicator based on whether people step is shown
  const getProgressStep = currentStep => {
    if (!shouldShowPeopleStep && currentStep > 2) {
      return 3; // Skip to final step
    }
    return currentStep;
  };
  return <div className="p-4 max-w-md mx-auto" data-id="element-426">
      {step < 4 && <div className="mb-6" data-id="element-427">
          <div className="flex justify-between mb-2" data-id="element-428">
            {Array.from({
          length: totalSteps - 1
        }).map((_, i) => <div key={i} className={`${shouldShowPeopleStep ? 'w-1/3' : 'w-1/2'} h-1 rounded-full mx-1 ${i < getProgressStep(step) ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-429"></div>)}
          </div>
          <div className="flex justify-between text-xs text-gray-500" data-id="element-430">
            <span data-id="element-431">Upload</span>
            <span data-id="element-432">Details</span>
            {shouldShowPeopleStep && <span data-id="element-433">People</span>}
          </div>
        </div>}
      {renderStepContent()}
      {renderNavButtons()}
      <FullSizeReceiptModal data-id="element-434" />
    </div>;
}